import React, { useRef, useMemo } from 'react';
import * as THREE from 'three';
import { Html } from '@react-three/drei';
import { calculateAngle } from '../../utils/geometry';

const POSE_CONNECTIONS = [
    [11, 12], // shoulders
    [11, 13], [13, 15], // left arm
    [12, 14], [14, 16], // right arm
    [11, 23], [12, 24], // trunk
    [23, 24], // hips
    [23, 25], [25, 27], // left leg
    [24, 26], [26, 28]  // right leg
];

const Joint = ({ position, color }) => (
    <mesh position={position}>
        <sphereGeometry args={[0.02, 16, 16]} />
        <meshStandardMaterial
            color={color}
            emissive={color}
            emissiveIntensity={0.5}
            roughness={0.2}
            metalness={0.8}
        />
    </mesh>
);

const Bone = ({ start, end, color }) => {
    // Calculate position, rotation and length
    const vecStart = new THREE.Vector3(...start);
    const vecEnd = new THREE.Vector3(...end);
    const length = vecStart.distanceTo(vecEnd);

    const midPoint = vecStart.add(vecEnd).multiplyScalar(0.5);

    const orientation = new THREE.Matrix4();
    const offsetRotation = new THREE.Matrix4();
    orientation.lookAt(vecStart, vecEnd, new THREE.Vector3(0, 1, 0));
    offsetRotation.makeRotationX(Math.PI / 2);
    orientation.multiply(offsetRotation);

    return (
        <mesh position={midPoint} quaternion={new THREE.Quaternion().setFromRotationMatrix(orientation)}>
            <cylinderGeometry args={[0.015, 0.015, length, 8]} />
            <meshStandardMaterial
                color={color}
                emissive={color}
                emissiveIntensity={0.2}
                transparent
                opacity={0.8}
            />
        </mesh>
    );
};



const AngleViz = ({ a, b, c, label, color }) => {
    if (!a || !b || !c) return null;
    const angle = calculateAngle({ x: a[0], y: a[1], z: a[2] }, { x: b[0], y: b[1], z: b[2] }, { x: c[0], y: c[1], z: c[2] });

    return (
        <Html position={b} center zIndexRange={[100, 0]}>
            <div className="flex flex-col items-center pointer-events-none">
                <div className="text-white font-bold text-sm bg-black/80 px-2 py-1 rounded shadow">
                    {angle}°
                </div>
            </div>
        </Html>
    );
};

const Avatar = ({ landmarks, role }) => {
    const points = useMemo(() => {
        if (!landmarks || landmarks.length === 0) return [];
        return landmarks.map(l => [l.x, -l.y, -l.z]);
    }, [landmarks]);

    // Grounding: Find lowest Y from FEET integration only (Indices 27-32)
    // 27: Left Ankle, 28: Right Ankle
    // 29: Left Heel, 30: Right Heel
    // 31: Left Foot Index, 32: Right Foot Index
    // Using simple min of these specific points is much more stable than full body min (which includes hands).
    const yOffset = useMemo(() => {
        if (points.length === 0) return 0;

        // Filter for specific foot/leg landmarks if they exist
        // Indices 27 to 32
        const feetPoints = points.filter((_, idx) => idx >= 27 && idx <= 32);

        // If feet aren't detected well, fallback to legs (23-26) + feet, or full body
        const groundSet = feetPoints.length > 0 ? feetPoints : points;

        if (groundSet.length === 0) return 0;

        const minY = Math.min(...groundSet.map(p => p[1]));
        return -minY;
    }, [points]);

    if (!landmarks || landmarks.length === 0) return null;

    return (
        <group position={[0, yOffset, 0]}>
            {/* Joints */}
            {points.map((pt, idx) => {
                if (idx > 32) return null;
                const isLeft = idx % 2 !== 0;
                return <Joint key={idx} position={pt} color={isLeft ? '#00ffff' : '#ff00ff'} />
            })}

            {/* Bones */}
            {POSE_CONNECTIONS.map(([start, end], idx) => {
                if (!points[start] || !points[end]) return null;
                return <Bone key={`bone-${idx}`} start={points[start]} end={points[end]} color="#ffffff" />
            })}

            {/* Angles */}
            {role === 'bowler' && (
                <>
                    {/* Front Leg Brace (Left or Right depending on handedness, usually 24-26-28 or 23-25-27) */}
                    {/* Assuming Right Arm Bowler -> Left Leg Plant. 23-25-27 is Left. 24-26-28 is Right. */}
                    {/* Let's visualize BOTH or just the one relevant? Stick to standard righty for now or show both */}
                    <AngleViz a={points[24]} b={points[26]} c={points[28]} color="#00ffff" label="Leg" />
                    <AngleViz a={points[12]} b={points[14]} c={points[16]} color="#ff00ff" label="Arm" />
                </>
            )}
            {role !== 'bowler' && ( // Batsman
                <>
                    <AngleViz a={points[11]} b={points[13]} c={points[15]} color="#00ffff" label="Elbow" />
                    <AngleViz a={points[23]} b={points[25]} c={points[27]} color="#ff00ff" label="Knee" />
                </>
            )}
        </group>
    );
};

export default Avatar;
